import { Stack } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import React, { FC, useEffect, useState } from 'react';
import { QbDesktopFieldMapping } from '../../providers/Quickbooks/QbDesktop.model';
import TwAlert from '../TwAlerts/TwAlerts';
import TwAutocomplete, { TwAutocompleteOption } from '../TwAutocomplete/TwAutocomplete';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import TwDataGrid, { TwTableData } from '../TwDataGrid/TwDataGrid';
import TwDialog from '../TwDialog/TwDialog';
import TwField from '../TwFields/TwFields';

interface TwQbDesktopFieldMappingProps {
  isOpen: boolean;
  onClose?: () => void;
  onSave?: (fieldMapping: QbDesktopFieldMapping) => void;
  fieldMapping?: QbDesktopFieldMapping;
}

interface TwQbDesktopFieldMappingOption extends TwAutocompleteOption {
  is_custom?: boolean;
  custom_value?: string;
  is_custom_value_required?: boolean;
  placeholder_text?: string;
}

const serviceMappingColumns: GridColDef[] = [
  { field: 'field', headerName: 'Field', hideSortIcons: true, flex: 0.3 },
  { field: 'qbdObject', headerName: 'QBD Object', hideSortIcons: true, flex: 0.3 },
  {
    field: 'tbField',
    headerName: 'Truckbase Field',
    hideSortIcons: true,
    resizable: false,
    flex: 0.8,
    renderCell: (params: GridRenderCellParams): React.ReactNode => {
      const { value } = params;
      return value;
    }
  },
  { field: 'description', headerName: 'Description', hideSortIcons: true, flex: 1 }
];

// references "server/src/interfaces/db/models/qb-desktop-config.ts" -> QbDesktopFieldMapping
const serviceMappingOptions: Record<string, TwQbDesktopFieldMappingOption[]> = {
  ['customer']: [
    { id: 'external_customer_id', label: 'Customer ID', value: 'external_customer_id' },
    { id: 'customer_full_name', label: 'fullName in Customer ID', value: 'customer_full_name' },
    { id: 'customer_name', label: 'Visible Customer Name', value: 'customer_name' }
  ],
  ['quantity']: [
    { id: 'invoice_quantity', label: 'Quantity on invoice line item', value: 'invoice_quantity' },
    { id: 'do_not_map', label: 'Do not map', value: 'do_not_map' }
  ],
  ['item']: [
    { id: 'external_service_id', label: 'Service ID', value: 'external_service_id' },
    { id: 'service_full_name', label: 'fullName in Service ID', value: 'service_full_name' },
    { id: 'service_name', label: 'Visible Service Name', value: 'service_name' },
    {
      id: 'service_single_id',
      label: 'Single Service ID',
      value: 'service_single_id',
      is_custom: true,
      is_custom_value_required: true
    }
  ],
  ['rate']: [
    { id: 'invoice_rate', label: 'Price on invoice line item', value: 'invoice_rate' },
    { id: 'do_not_map', label: 'Do not map', value: 'do_not_map' }
  ],
  ['amount']: [
    { id: 'invoice_amount', label: 'Total amount on invoice line item', value: 'invoice_amount' },
    { id: 'do_not_map', label: 'Do not map', value: 'do_not_map' }
  ],
  ['transaction_date']: [
    { id: 'invoice_created_at', label: 'Invoice creation date', value: 'invoice_created_at' },
    { id: 'order_pickup_date', label: 'Order pickup date', value: 'order_pickup_date' },
    { id: 'order_delivery_date', label: 'Order delivery date', value: 'order_delivery_date' }
  ],
  ['due_date']: [
    { id: 'invoice_due_date', label: 'Due Date', value: 'invoice_due_date' },
    { id: 'do_not_map', label: 'Do not map', value: 'do_not_map' }
  ],
  ['class']: [
    {
      id: 'class_id',
      label: 'Single Class ID',
      value: 'class_id',
      is_custom: true,
      is_custom_value_required: true
    },
    {
      id: 'class_name',
      label: 'Single Class Name',
      value: 'class_name',
      is_custom: true,
      is_custom_value_required: true
    },
    {
      id: 'truck_name',
      label: 'Truck Name or Number',
      value: 'truck_name',
      is_custom: true,
      is_custom_value_required: false,
      placeholder_text: 'Prefix (optional)'
    },
    { id: 'do_not_map', label: 'Do not map', value: 'do_not_map' }
  ],
  ['po_number']: [
    { id: 'customer_order_number', label: 'Customer order number', value: 'customer_order_number' },
    { id: 'order_number', label: 'Order number', value: 'order_number' },
    { id: 'do_not_map', label: 'Do not map', value: 'do_not_map' }
  ],
  ['memo']: [
    { id: 'description', label: 'Description', value: 'description' },
    { id: 'do_not_map', label: 'Do not map', value: 'do_not_map' }
  ],
  ['ship_date']: [
    { id: 'invoice_created_at', label: 'Invoice creation date', value: 'invoice_created_at' },
    { id: 'order_pickup_date', label: 'Order pickup date', value: 'order_pickup_date' },
    { id: 'order_delivery_date', label: 'Order delivery date', value: 'order_delivery_date' },
    { id: 'do_not_map', label: 'Do not map', value: 'do_not_map' }
  ],
  ['ar_account']: [
    {
      id: 'ar_account_name',
      label: 'AR Account Name',
      value: 'ar_account_name',
      is_custom: true,
      is_custom_value_required: true
    },
    { id: 'do_not_map', label: 'Do not map', value: 'do_not_map' }
  ],
  ['sales_tax']: [
    { id: 'sales_tax_id', label: 'Sales Tax ID', value: 'sales_tax_id' },
    { id: 'sales_tax_name', label: 'Sales Tax Name', value: 'sales_tax_name' },
    { id: 'do_not_map', label: 'Do not map', value: 'do_not_map' }
  ]
};

const TwQbDesktopFieldMapping: FC<TwQbDesktopFieldMappingProps> = ({
  isOpen,
  onClose,
  onSave,
  fieldMapping
}): JSX.Element => {
  const [newFieldMapping, setNewFieldMapping] = useState<QbDesktopFieldMapping>({});
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    setNewFieldMapping(fieldMapping || {});
  }, [fieldMapping]);

  const handleClose = (): void => {
    setErrors([]);
    onClose && onClose();
  };

  const handleSubmit = (): void => {
    try {
      let hasErrors = false;
      Object.keys(newFieldMapping).forEach((field) => {
        if (
          serviceMappingOptions[field].find(
            (option) => option.value === newFieldMapping[field]?.value
          )?.is_custom_value_required === true &&
          newFieldMapping[field]?.is_custom &&
          !newFieldMapping[field]?.custom_value
        ) {
          setErrors([
            `${
              serviceMappingOptions[field].find(
                (option) => option.value === newFieldMapping[field]?.value
              )?.label
            } is required.`
          ]);
          hasErrors = true;
        }
      });

      if (!hasErrors) {
        onSave && onSave(newFieldMapping);
        handleClose();
      }
    } catch (error) {
      console.error('Error saving QBD field mapping.', error);
      setErrors(['Error saving QBD field mapping.']);
    }
  };

  const handleDropdownSelectionChange =
    (field: string) =>
    (event: any, value: any): void => {
      setNewFieldMapping((prev) => ({
        ...prev,
        [field]: {
          value: value.value,
          is_custom: value.is_custom,
          custom_value: value.is_custom ? prev[field]?.custom_value : undefined,
          is_custom_value_required: value.is_custom_value_required
        }
      }));
    };

  const renderFieldMappingElements = (field: string): React.ReactNode => {
    return (
      <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
        <TwAutocomplete
          name={field}
          sx={{ marginTop: '8px' }}
          optionList={serviceMappingOptions[field]}
          value={
            serviceMappingOptions[field].find(
              (option) => option.value === newFieldMapping[field]?.value
            ) || null
          }
          onChange={handleDropdownSelectionChange(field)}
          disableClearable={true}
          sortList={false}
        />
        {/* show the custom text input for custom value */}
        {newFieldMapping[field]?.is_custom && (
          <TwField
            onKeyDown={(e: any): void => e.stopPropagation()}
            value={newFieldMapping[field]?.custom_value}
            name={`${field}-custom`}
            sx={{ marginTop: '8px', width: 225 }}
            fullWidth={true}
            onInputChange={(e): void => {
              setNewFieldMapping((prev) => ({
                ...prev,
                [field]: {
                  ...prev[field],
                  custom_value: e.target.value
                }
              }));
            }}
            error={
              serviceMappingOptions[field].find(
                (option) => option.value === newFieldMapping[field]?.value
              )?.is_custom_value_required === true &&
              newFieldMapping[field]?.is_custom &&
              !newFieldMapping[field]?.custom_value
            }
            placeholder={
              serviceMappingOptions[field].find(
                (option) => option.value === newFieldMapping[field]?.value
              )?.placeholder_text
            }
          />
        )}
      </Stack>
    );
  };

  const rows: TwTableData[] = [
    {
      id: 'customer',
      field: 'Customer',
      qbdObject: '-',
      tbField: renderFieldMappingElements('customer'),
      description: 'Maps Customers by Customer ID or Customer name. Cannot be empty.'
    },
    {
      id: 'item',
      field: 'Item',
      qbdObject: 'ItemRef',
      tbField: renderFieldMappingElements('item'),
      description: 'Each invoice line item; can pass TB service name or external ID to an item.'
    },
    {
      id: 'quantity',
      field: 'Quantity',
      qbdObject: 'Quantity',
      tbField: renderFieldMappingElements('quantity'),
      description:
        'If a service specifies Quantity and Rate but not Amount, QBD will calculate the Amount.'
    },
    {
      id: 'rate',
      field: 'Rate',
      qbdObject: 'Rate',
      tbField: renderFieldMappingElements('rate'),
      description:
        'If a service specifies Quantity and Amount but not Rate, QBD will calculate the Rate.'
    },
    {
      id: 'amount',
      field: 'Amount',
      qbdObject: 'Amount',
      tbField: renderFieldMappingElements('amount'),
      description: ''
    },
    {
      id: 'transaction_date',
      field: 'Transaction Date',
      qbdObject: 'TxnDate',
      tbField: renderFieldMappingElements('transaction_date'),
      description: 'Date of the invoice.'
    },
    {
      id: 'due_date',
      field: 'Due Date',
      qbdObject: 'DueDate',
      tbField: renderFieldMappingElements('due_date'),
      description: 'Due date of the invoice.'
    },
    {
      id: 'class',
      field: 'Class',
      qbdObject: 'Class',
      tbField: renderFieldMappingElements('class'),
      description:
        'Classes can be used to separate transactions into meaningful categories. "Truck Name or Number" provides an optional field for a Full Name prefix e.g. "Trucking:Heavy Haul".'
    },
    {
      id: 'po_number',
      field: 'PO Number',
      qbdObject: 'PONumber',
      tbField: renderFieldMappingElements('po_number'),
      description: 'Purchase order number.'
    },
    {
      id: 'memo',
      field: 'Notes',
      qbdObject: 'Desc',
      tbField: renderFieldMappingElements('memo'),
      description: 'Description at invoice line item level.'
    },
    {
      id: 'ship_date',
      field: 'Ship Date',
      qbdObject: 'ShipDate',
      tbField: renderFieldMappingElements('ship_date'),
      description: 'Select the date from Truckbase to map to Ship Date field in QuickBooks.'
    },
    {
      id: 'ar_account',
      field: 'AR Account',
      qbdObject: 'ARAccountRef',
      tbField: renderFieldMappingElements('ar_account'),
      description: 'Account Receivable in QuickBooks. Uses default account if not mapped.'
    },
    {
      id: 'sales_tax',
      field: 'Sales Tax',
      qbdObject: 'SalesTax',
      tbField: renderFieldMappingElements('sales_tax'),
      description: 'Sales tax in QuickBooks.'
    }
  ];

  return (
    <TwDialog
      open={isOpen}
      onClickClose={handleClose}
      title="QuickBooks Desktop Configuration"
      actions={[
        <TwButton key="1" type={TwButtonType.secondary} label="Cancel" action={handleClose} />,
        <TwButton key="2" type={TwButtonType.primary} label="Confirm" action={handleSubmit} />
      ]}
      fullScreen
    >
      {errors.length > 0 && errors.map((error, i) => <TwAlert key={i} message={error} />)}
      <TwDataGrid
        columns={serviceMappingColumns}
        rows={rows}
        hideFooter
        maxHeight="none"
        disableRowSelectionOnClick
      />
    </TwDialog>
  );
};

export default TwQbDesktopFieldMapping;
